<!--
    Notification List

    <NotificationList
      :notification-list="..."      # MANDATORY - list of notifications
      :has-next-page="..."          # OPTIONAL - is there a next page after the current page
      @update-page="..."      - fired on scroll for page update
      @close
    </NotificationList>
-->
<script setup lang="ts">
/* COMPONENTS */
import { ALDivider, ALLoader } from '@af-lib/vue-ui';
import { useIntersectionObserver } from '@vueuse/core';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { useDeleteNotification, useUpdateNotification } from '@/api/vue-query/notifications/notifications.mutations';
import type { MeNotifications } from '@/types/api/me/me';
/* TYPES */
import type { Notification } from '@/types/notifications/notification';

/* CONFIG */
import { getNotifConfig } from './notificationLayout.config';
import NotificationLayout from './NotificationLayout.vue';

/* PROPS */
type Props = {
  notificationList: Notification[];
  hasNextPage?: boolean;
};
const props = defineProps<Props>();
const router = useRouter();
const bottomLoader = ref<HTMLElement | null>(null);

/* EMITS */
const emits = defineEmits<{
  (e: 'update:page'): void;
  (e: 'close'): void;
}>();

/* FUNCTIONS */
/**
 * Function called when the bottom loader is visible to trigger a call for the next page
 *
 * @param {Boolean} isVisible : true if the element is visible
 */
function onBottomReached(isVisible: boolean) {
  if (isVisible) {
    emits('update:page');
  }
}

useIntersectionObserver(bottomLoader, ([entry]) => {
  onBottomReached(entry?.isIntersecting ?? false);
});

/* API */
const { mutate: deleteNotifs, isPending: deletingNotif } = useDeleteNotification();
const { mutate: updateNotif } = useUpdateNotification();

/**
 * From received notification clicked, redirect to the right external or internal URL
 *
 * @param {Notification} notification Notification that triggers a redirection
 */
function notificationRedirection(notification: Notification) {
  // Get redirection link if available on click
  const { link, route } = getNotifConfig(notification.type);

  if (link) {
    window.location.assign(link(notification));
  }
  // Phoenix internal route
  if (route) {
    router.push(route(notification));
  }
}

function deleteNotif(uuid: string) {
  deleteNotifs([uuid]);
}

/**
 * If a notification is clicked, set new status
 */
function onUpdateNotification(notification: MeNotifications) {
  if (deletingNotif.value) return;

  // Set status of notif inside list to clicked
  if (notification.status !== 'clicked') {
    updateNotif({ uuids: [notification.uuid], status: 'clicked' });
  }
  notificationRedirection(notification);

  // Close menu
  emits('close');
}
</script>

<template>
  <!-- Notification list infinite -->
  <ul
    class="m-0 min-h-0 flex-1 list-none overflow-auto p-0"
  >
    <template v-for="notification in props.notificationList" :key="notification.uuid">
      <!-- V for on template to check if the conrresponding type exist -->
      <li
        v-if="getNotifConfig(notification.type)"
        :disabled="deletingNotif"
        :class="[
          notification.status !== 'clicked'
            ? 'bg-af-blue-100 hover:bg-af-blue-200'
            : 'hover:bg-af-grey-100',
          deletingNotif ? 'pointer-events-none opacity-60' : 'cursor-pointer'
        ]"
        :aria-label="getNotifConfig(notification.type)?.ariaLabel(notification)"
        @click.stop="onUpdateNotification(notification)"
      >
        <NotificationLayout
          :notification="notification"
          :notificationConfig="getNotifConfig(notification.type)"
          @click:trash="deleteNotif(notification.uuid)"
        ></NotificationLayout>
        <ALDivider type="horizontal" />
      </li>
    </template>
    <!-- Loader when fetching page -->
    <li
      v-if="hasNextPage && props.notificationList.length !== 0"
      ref="bottomLoader"
      class="flex justify-center py-2"
    >
      <ALLoader size="s" />
    </li>
  </ul>
</template>
